import {
  DarwinButton,
  DarwinSelect,
  DarwinListView,
} from 'components/darwin-ui';
import {
  ProcessField,
  ProcessForm,
  CustomProcessField,
} from 'types/dataTableTypes';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomFieldColumns } from 'hooks/dataTable/useFieldColumns';
import { useEmptyState } from 'hooks/dataTable/useEmptyState';
import { ColumnAction } from './DataFields';
import Icon from 'components/darwin-ui/Icon';
import {
  DTablePaginationConfig,
  DTableFilterValue,
  DTableSorterResult,
} from '@darwin-ui';

interface CustomFieldsTableProps {
  processId: string;
  customFields: CustomProcessField[];
  forms: ProcessForm[];
  isLoading: boolean;
  selectedFormId?: string;
  onFormSelect: (formId: string) => void;
  onAddField: () => void;
  tableControls: {
    currentPage?: number;
    pageSize?: number;
    searchQuery?: string;
    setSearchQuery?: (query: string) => void;
    onChange?: (
      pagination: DTablePaginationConfig,
      filters: Record<string, DTableFilterValue | null>,
      sorter:
        | DTableSorterResult<ProcessField>
        | DTableSorterResult<ProcessField>[]
    ) => void;
  };
  actions: ColumnAction[];
}

export const CustomFieldsTable: React.FC<CustomFieldsTableProps> = ({
  processId,
  customFields,
  forms,
  isLoading,
  selectedFormId,
  onFormSelect,
  onAddField,
  tableControls,
  actions,
}) => {
  const { t } = useTranslation();

  // Filter custom fields based on selected form
  const filteredCustomFields = useMemo(() => {
    if (!selectedFormId) return customFields;

    // Check if any field has the formIds property
    const hasFormIdsProperty = customFields.some(field => field.formIds);

    // If fields don't have formIds property, assume API already filtered the data
    if (!hasFormIdsProperty) {
      return customFields;
    }

    // Only apply client-side filtering if formIds property exists
    return customFields.filter(field =>
      field.formIds?.includes(selectedFormId)
    );
  }, [customFields, selectedFormId]);

  // Check if there are no forms
  const hasNoForms = useMemo(() => {
    return forms.length === 0;
  }, [forms]);

  // Check if there are no custom fields for the selected form
  const hasNoCustomFieldsForForm = useMemo(() => {
    return !!(selectedFormId && filteredCustomFields.length === 0);
  }, [filteredCustomFields, selectedFormId]);

  // Form selector options
  const formOptions = useMemo(
    () =>
      forms.map(form => ({
        value: form.id,
        label: form.name,
      })),
    [forms]
  );

  // Custom columns using the custom fields hook
  const columns = useCustomFieldColumns(actions, tableControls.searchQuery);

  // Header buttons containing form selector and add button
  const headerButtons = useMemo(
    () => (
      <>
        <DarwinSelect
          value={selectedFormId}
          placeholder={t('data-tables.custom-fields.select-form-placeholder')}
          style={{ width: 250 }}
          onChange={onFormSelect}
          disabled={hasNoForms}
          options={formOptions}
          allowClear
          showSearch
          optionFilterProp="label"
        />
        {/* Add Field button */}
        <DarwinButton
          type="primary"
          onClick={hasNoForms ? undefined : onAddField}
          style={{
            cursor: hasNoForms ? 'not-allowed' : 'pointer',
            opacity: hasNoForms ? 0.5 : 1,
          }}
        >
          <Icon name="plus" />
          <span>{t('data-tables.add-new-field')}</span>
        </DarwinButton>
      </>
    ),
    [selectedFormId, onFormSelect, onAddField, hasNoForms, formOptions, t]
  );

  // Empty state text
  const emptyText = useEmptyState.useCustomFieldsEmptyState(
    hasNoForms,
    hasNoCustomFieldsForForm
  );

  return (
    <DarwinListView<ProcessField>
      data={filteredCustomFields}
      isLoading={isLoading}
      columns={columns}
      emptyText={emptyText}
      header={{
        title: t('data-tables.custom-fields.title'),
        actions: headerButtons,
        setSearchQuery: tableControls.setSearchQuery,
      }}
      table={{ onChange: tableControls.onChange, scrollable: false }}
    />
  );
};
